import React, { useState, useEffect } from 'react';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Bell, Plus, Trash2, Clock, Target, AlertTriangle,
  TrendingDown, UserX, History, Settings, Zap, RefreshCw,
  ChevronDown, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPO_ICONS = {
  operador_parado: UserX,
  of_quase_concluida: Target,
  excesso_anomalias: AlertTriangle,
  producao_abaixo_objetivo: TrendingDown,
};

const TIPO_COLORS = {
  operador_parado: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: 'text-orange-500', accent: 'bg-orange-500' },
  of_quase_concluida: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'text-blue-500', accent: 'bg-blue-500' },
  excesso_anomalias: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'text-red-500', accent: 'bg-red-500' },
  producao_abaixo_objetivo: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'text-amber-500', accent: 'bg-amber-500' },
};

function RuleCard({ regra, tipos, onToggle, onEdit, onDelete }) {
  const meta = tipos[regra.tipo] || {};
  const Icon = TIPO_ICONS[regra.tipo] || Bell;
  const colors = TIPO_COLORS[regra.tipo] || { bg: 'bg-stone-50', border: 'border-stone-200', text: 'text-stone-700', icon: 'text-stone-500', accent: 'bg-stone-500' };

  const formatDate = (iso) => {
    if (!iso) return 'Nunca';
    return new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Card
      data-testid={`rule-card-${regra.tipo}`}
      className={`overflow-hidden border-2 transition-all ${regra.ativo ? colors.border : 'border-stone-200 opacity-60'}`}
    >
      <div className={`h-1.5 ${regra.ativo ? colors.accent : 'bg-stone-300'}`} />
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${colors.bg}`}>
              <Icon className={`h-6 w-6 ${colors.icon}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-stone-900">{meta.label || regra.tipo}</h3>
                {regra.ativo ? (
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-700">ATIVA</span>
                ) : (
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-stone-100 text-stone-500">INATIVA</span>
                )}
              </div>
              <p className="text-sm text-stone-500 mb-3">{meta.descricao}</p>

              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${colors.bg}`}>
                  <Settings className={`h-3.5 w-3.5 ${colors.icon}`} />
                  <span className={`font-semibold ${colors.text}`}>
                    {regra.valor} {meta.param_unit}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-stone-500">
                  <Zap className="h-3.5 w-3.5" />
                  <span>{regra.total_triggers || 0} disparos</span>
                </div>
                <div className="flex items-center gap-1.5 text-stone-500">
                  <Clock className="h-3.5 w-3.5" />
                  <span>Último: {formatDate(regra.ultimo_trigger)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <Switch
              data-testid={`toggle-${regra.tipo}`}
              checked={regra.ativo}
              onCheckedChange={() => onToggle(regra)}
            />
            <Button variant="ghost" size="sm" onClick={() => onEdit(regra)} data-testid={`edit-rule-${regra.tipo}`}>
              <Settings className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(regra)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-rule-${regra.tipo}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function RegrasNotificacoes({ embedded = false }) {
  const [regras, setRegras] = useState([]);
  const [tipos, setTipos] = useState({});
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showHistorico, setShowHistorico] = useState(false);

  const [formData, setFormData] = useState({
    tipo: '',
    valor: 0,
    ativo: true,
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [regrasRes, tiposRes, histRes] = await Promise.all([
        axios.get(`${API}/push-rules`),
        axios.get(`${API}/push-rules/tipos`),
        axios.get(`${API}/push-rules/historico`),
      ]);
      setRegras(regrasRes.data);
      setTipos(tiposRes.data);
      setHistorico(histRes.data);
    } catch (e) {
      console.error('Erro:', e);
      toast.error('Erro ao carregar regras');
    } finally {
      setLoading(false);
    }
  };

  const tiposDisponiveis = Object.keys(tipos).filter(
    t => !regras.find(r => r.tipo === t) || editingRule?.tipo === t
  );

  const openCreate = () => {
    const firstAvailable = tiposDisponiveis[0] || '';
    setEditingRule(null);
    setFormData({
      tipo: firstAvailable,
      valor: firstAvailable ? tipos[firstAvailable]?.param_default || 0 : 0,
      ativo: true,
    });
    setShowDialog(true);
  };

  const openEdit = (regra) => {
    setEditingRule(regra);
    setFormData({
      tipo: regra.tipo,
      valor: regra.valor,
      ativo: regra.ativo,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.tipo) {
      toast.error('Selecione um tipo de regra');
      return;
    }
    setSaving(true);
    try {
      if (editingRule) {
        await axios.put(`${API}/push-rules/${editingRule.id}`, {
          valor: formData.valor,
          ativo: formData.ativo,
        });
        toast.success('Regra atualizada!');
      } else {
        await axios.post(`${API}/push-rules`, formData);
        toast.success('Regra criada!');
      }
      setShowDialog(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (regra) => {
    try {
      await axios.put(`${API}/push-rules/${regra.id}/toggle`);
      toast.success(regra.ativo ? 'Regra desativada' : 'Regra ativada');
      loadAll();
    } catch (e) {
      toast.error('Erro ao alterar estado');
    }
  };

  const handleDelete = async (regra) => {
    if (!window.confirm(`Eliminar a regra "${tipos[regra.tipo]?.label || regra.tipo}"?`)) return;
    try {
      await axios.delete(`${API}/push-rules/${regra.id}`);
      toast.success('Regra eliminada');
      loadAll();
    } catch (e) {
      toast.error('Erro ao eliminar');
    }
  };

  const selectedMeta = tipos[formData.tipo] || {};

  const content = (
      <div className="space-y-6" data-testid="regras-notificacoes-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-stone-900 flex items-center gap-2">
              <Bell className="h-6 w-6 text-primary" />
              Regras de Notificação
            </h1>
            <p className="text-stone-500 text-sm mt-1">
              Configure alertas automáticos por push — avaliados a cada 5 minutos
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadAll} disabled={loading} data-testid="refresh-rules">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
            <Button onClick={openCreate} disabled={tiposDisponiveis.length === 0} data-testid="btn-nova-regra">
              <Plus className="h-4 w-4 mr-2" /> Nova Regra
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 text-stone-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Info banner */}
            <Card className="p-4 bg-primary/5 border-primary/20">
              <div className="flex items-start gap-3">
                <Zap className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-stone-800">
                    As regras são avaliadas automaticamente a cada 5 minutos pelo servidor.
                  </p>
                  <p className="text-xs text-stone-500 mt-1">
                    Quando uma condição é satisfeita, uma notificação push é enviada para todos os gestores e admins. Cada regra tem um cooldown de 1 hora para evitar spam.
                  </p>
                </div>
              </div>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="p-4 text-center">
                <p className="text-3xl font-bold text-stone-900">{regras.length}</p>
                <p className="text-xs text-stone-500 font-medium">Regras Configuradas</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="text-3xl font-bold text-emerald-600">{regras.filter(r => r.ativo).length}</p>
                <p className="text-xs text-stone-500 font-medium">Ativas</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="text-3xl font-bold text-amber-600">{regras.reduce((a, r) => a + (r.total_triggers || 0), 0)}</p>
                <p className="text-xs text-stone-500 font-medium">Total de Disparos</p>
              </Card>
            </div>

            {/* Rules list */}
            {regras.length === 0 ? (
              <Card className="p-12 text-center">
                <Bell className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-stone-700 mb-2">Sem regras configuradas</h3>
                <p className="text-sm text-stone-500 mb-4">Crie a primeira regra para receber alertas automáticos no telemóvel.</p>
                <Button onClick={openCreate} data-testid="btn-nova-regra-empty">
                  <Plus className="h-4 w-4 mr-2" /> Criar Primeira Regra
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {regras.map(regra => (
                  <RuleCard
                    key={regra.id}
                    regra={regra}
                    tipos={tipos}
                    onToggle={handleToggle}
                    onEdit={openEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}

            {/* Histórico */}
            <Card className="overflow-hidden">
              <button
                onClick={() => setShowHistorico(!showHistorico)}
                className="w-full flex items-center justify-between p-4 hover:bg-stone-50 transition-colors"
                data-testid="toggle-historico"
              >
                <div className="flex items-center gap-2">
                  <History className="h-5 w-5 text-stone-600" />
                  <span className="font-semibold text-stone-900">Histórico de Disparos</span>
                  <span className="text-sm text-stone-500">({historico.length})</span>
                </div>
                {showHistorico ? <ChevronDown className="h-4 w-4 text-stone-400" /> : <ChevronRight className="h-4 w-4 text-stone-400" />}
              </button>
              {showHistorico && (
                <div className="border-t divide-y">
                  {historico.length === 0 ? (
                    <p className="p-6 text-sm text-stone-500 text-center">Nenhum disparo registado ainda.</p>
                  ) : (
                    historico.map((log, i) => {
                      const Icon = TIPO_ICONS[log.tipo] || Bell;
                      const colors = TIPO_COLORS[log.tipo] || {};
                      return (
                        <div key={log.id || i} className="px-4 py-3 flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.bg || 'bg-stone-50'}`}>
                            <Icon className={`h-4 w-4 ${colors.icon || 'text-stone-500'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-stone-800 truncate">{log.titulo}</p>
                            <p className="text-xs text-stone-500 truncate">{log.corpo}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs text-stone-500">
                              {new Date(log.timestamp).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </p>
                            <p className="text-xs text-emerald-600">{log.push_enviadas} pushes</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </Card>
          </>
        )}

        {/* Dialog Criar/Editar */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingRule ? 'Editar Regra' : 'Nova Regra de Notificação'}</DialogTitle>
              <p className="text-sm text-stone-500">Configure os parâmetros da regra automática</p>
            </DialogHeader>
            <div className="space-y-5 py-4">
              {/* Tipo */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Tipo de Regra</label>
                {editingRule ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-stone-50 border">
                    {(() => {
                      const Icon = TIPO_ICONS[formData.tipo] || Bell;
                      return <Icon className="h-4 w-4 text-stone-600" />;
                    })()}
                    <span className="text-sm font-medium">{tipos[formData.tipo]?.label || formData.tipo}</span>
                  </div>
                ) : (
                  <Select
                    value={formData.tipo}
                    onValueChange={(val) => {
                      setFormData({
                        ...formData,
                        tipo: val,
                        valor: tipos[val]?.param_default || 0,
                      });
                    }}
                  >
                    <SelectTrigger data-testid="select-tipo-regra">
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposDisponiveis.map(t => {
                        const Icon = TIPO_ICONS[t] || Bell;
                        return (
                          <SelectItem key={t} value={t}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              <span>{tipos[t]?.label}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
                {formData.tipo && (
                  <p className="text-xs text-stone-500 mt-1">{tipos[formData.tipo]?.descricao}</p>
                )}
              </div>

              {/* Valor */}
              {formData.tipo && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    {selectedMeta.param_label || 'Valor'}
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="input-valor-regra"
                      type="number"
                      value={formData.valor}
                      onChange={e => setFormData({ ...formData, valor: parseInt(e.target.value) || 0 })}
                      min={selectedMeta.param_min}
                      max={selectedMeta.param_max}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium text-stone-500 w-10">{selectedMeta.param_unit}</span>
                  </div>
                  <p className="text-xs text-stone-400 mt-1">
                    Mín: {selectedMeta.param_min} — Máx: {selectedMeta.param_max}
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="btn-cancel-rule">Cancelar</Button>
              <Button onClick={handleSave} disabled={saving || !formData.tipo} data-testid="btn-save-rule">
                {saving ? 'A guardar...' : (editingRule ? 'Atualizar' : 'Criar Regra')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
  );

  return embedded ? content : <GestorLayout>{content}</GestorLayout>;
}
